import BaseActionCableConnector from '../../shared/helpers/BaseActionCableConnector';
import { playNewMessageNotificationInWidget } from 'widget/helpers/WidgetAudioNotificationHelper';
import { ON_AGENT_MESSAGE_RECEIVED } from '../constants/widgetBusEvents';
import { IFrameHelper } from 'widget/helpers/utils';
import { shouldTriggerMessageUpdateEvent } from './IframeEventHelper';
import { CHATWOOT_ON_MESSAGE } from '../constants/sdkEvents';
import { emitter } from '../../shared/helpers/mitt';

const isMessageInActiveConversation = (getters, message) => {
  const { conversation_id: conversationId } = message;
  const activeConversationId =
    getters['conversationAttributes/getConversationParams'].id;
  return activeConversationId && conversationId !== activeConversationId;
};

class ActionCableConnector extends BaseActionCableConnector {
  constructor(app, pubsubToken) {
    super(app, pubsubToken);
    this.chunkSequences = {}; // Track expected sequences per message
    this.queuedChunks = {}; // Queue chunks for later processing
    this.streamingHeartbeats = {}; // Track heartbeat timeouts for streaming messages
    this.events = {
      'message.created': this.onMessageCreated,
      'message.updated': this.onMessageUpdated,
      'conversation.typing_on': this.onTypingOn,
      'conversation.typing_off': this.onTypingOff,
      'conversation.status_changed': this.onStatusChange,
      'conversation.created': this.onConversationCreated,
      'presence.update': this.onPresenceUpdate,
      'contact.merged': this.onContactMerge,
      // AI streaming events
      ai_streaming_started: this.onAiStreamingStarted,
      ai_streaming_chunk: this.onAiStreamingChunk,
      ai_streaming_completed: this.onAiStreamingCompleted,
      ai_streaming_error: this.onAiStreamingError,
    };
  }

  onDisconnected = () => {
    this.setLastMessageId();
  };

  onReconnect = () => {
    this.syncLatestMessages();

    // Process any queued chunks for all messages
    Object.keys(this.queuedChunks).forEach(messageId => {
      this.processQueuedChunks(messageId);
    });
  };

  setLastMessageId = () => {
    this.app.$store.dispatch('conversation/setLastMessageId');
  };

  syncLatestMessages = () => {
    this.app.$store.dispatch('conversation/syncLatestMessages');
  };

  onStatusChange = data => {
    if (data.status === 'resolved') {
      this.app.$store.dispatch('campaign/resetCampaign');
    }
    this.app.$store.dispatch('conversationAttributes/update', data);
  };

  onMessageCreated = data => {
    if (isMessageInActiveConversation(this.app.$store.getters, data)) {
      return;
    }

    this.app.$store
      .dispatch('conversation/addOrUpdateMessage', data)
      .then(() => emitter.emit(ON_AGENT_MESSAGE_RECEIVED));

    IFrameHelper.sendMessage({
      event: 'onEvent',
      eventIdentifier: CHATWOOT_ON_MESSAGE,
      data,
    });
    if (data.sender_type === 'User') {
      playNewMessageNotificationInWidget();
    }
  };

  onMessageUpdated = data => {
    if (isMessageInActiveConversation(this.app.$store.getters, data)) {
      return;
    }

    if (shouldTriggerMessageUpdateEvent(data)) {
      IFrameHelper.sendMessage({
        event: 'onEvent',
        eventIdentifier: CHATWOOT_ON_MESSAGE,
        data,
      });
    }

    this.app.$store.dispatch('conversation/addOrUpdateMessage', data);
  };

  onConversationCreated = () => {
    this.app.$store.dispatch('conversationAttributes/getAttributes');
  };

  onPresenceUpdate = data => {
    this.app.$store.dispatch('agent/updatePresence', data.users);
  };

  // eslint-disable-next-line class-methods-use-this
  onContactMerge = data => {
    const { pubsub_token: pubsubToken } = data;
    ActionCableConnector.refreshConnector(pubsubToken);
  };

  onTypingOn = data => {
    const activeConversationId =
      this.app.$store.getters['conversationAttributes/getConversationParams']
        .id;
    const isUserTypingOnAnotherConversation =
      data.conversation && data.conversation.id !== activeConversationId;

    if (isUserTypingOnAnotherConversation || data.is_private) {
      return;
    }
    this.clearTimer();
    this.app.$store.dispatch('conversation/toggleAgentTyping', {
      status: 'on',
    });
    this.initTimer();
  };

  onTypingOff = () => {
    this.clearTimer();
    this.app.$store.dispatch('conversation/toggleAgentTyping', {
      status: 'off',
    });
  };

  clearTimer = () => {
    if (this.CancelTyping) {
      clearTimeout(this.CancelTyping);
      this.CancelTyping = null;
    }
  };

  initTimer = () => {
    // Turn off typing automatically after 30 seconds
    this.CancelTyping = setTimeout(() => {
      this.onTypingOff();
    }, 30000);
  };

  // AI Streaming event handlers
  onAiStreamingStarted = data => {
    if (isMessageInActiveConversation(this.app.$store.getters, data)) {
      return;
    }

    // Store the message data but don't add to conversation yet
    // We'll add it when the first chunk arrives
    this.pendingStreamingMessage = {
      ...data,
      streaming: true,
      content: '', // Start with empty content
    };

    // Start heartbeat monitoring
    this.startStreamingHeartbeat(data.id);

    // Reset flag for typing indicator hiding
    this.typingIndicatorHidden = false;
  };

  onAiStreamingChunk = data => {
    if (isMessageInActiveConversation(this.app.$store.getters, data)) {
      return;
    }

    // Check connection state before processing chunk
    if (!this.consumer.connection.isOpen()) {
      console.warn(
        '[ActionCable] Connection not open, queuing chunk for later processing'
      );
      this.queueChunkForLater(data);
      return;
    }

    // Validate chunk sequence if available
    if (data.chunk_sequence !== undefined) {
      if (!this.isValidChunkSequence(data.id, data.chunk_sequence)) {
        console.warn(
          `[ActionCable] Out-of-order chunk detected: expected ${this.getExpectedSequence(data.id)}, got ${data.chunk_sequence}`
        );
        this.queueChunkForLater(data);
        return;
      }
      this.updateChunkSequence(data.id, data.chunk_sequence);
    }

    // Add the message to conversation on first chunk if it hasn't been added yet
    if (
      this.pendingStreamingMessage &&
      this.pendingStreamingMessage.id === data.id
    ) {
      this.app.$store.dispatch(
        'conversation/addOrUpdateMessage',
        this.pendingStreamingMessage
      );
      this.pendingStreamingMessage = null;
    }

    // Hide typing indicator on first chunk only
    if (!this.typingIndicatorHidden) {
      this.app.$store.dispatch('conversation/toggleAgentTyping', {
        status: 'off',
      });
      this.typingIndicatorHidden = true;
    }

    // Update the message content with the new chunk
    this.app.$store.dispatch('conversation/updateMessageContent', {
      id: data.id,
      content: data.current_content,
      contentDelta: data.content_delta,
      streaming: true,
    });

    // Reset heartbeat on each chunk
    this.startStreamingHeartbeat(data.id);

    // Auto-scroll immediately on every chunk
    this.app.scrollConversationToBottom();
  };

  onAiStreamingCompleted = data => {
    if (isMessageInActiveConversation(this.app.$store.getters, data)) {
      return;
    }

    // Add message if it wasn't added during streaming, otherwise just update it
    if (
      this.pendingStreamingMessage &&
      this.pendingStreamingMessage.id === data.id
    ) {
      // Add the message with streaming: false
      this.app.$store.dispatch('conversation/addOrUpdateMessage', {
        ...this.pendingStreamingMessage,
        ...data,
        streaming: false,
      });
      this.pendingStreamingMessage = null;
    } else {
      // Just update the existing message
      this.app.$store.dispatch('conversation/addOrUpdateMessage', {
        ...data,
        streaming: false,
      });
    }

    // Hide typing indicator and reset flag
    this.app.$store.dispatch('conversation/toggleAgentTyping', {
      status: 'off',
    });
    this.typingIndicatorHidden = false;

    // Clean up streaming state
    this.cleanupStreamingState(data.id);

    this.app.scrollConversationToBottom();

    // Trigger completion events
    emitter.emit(ON_AGENT_MESSAGE_RECEIVED);
    IFrameHelper.sendMessage({
      event: 'onEvent',
      eventIdentifier: CHATWOOT_ON_MESSAGE,
      data,
    });

    if (data.sender_type === 'User') {
      playNewMessageNotificationInWidget();
    }
  };

  onAiStreamingError = data => {
    if (isMessageInActiveConversation(this.app.$store.getters, data)) {
      return;
    }

    // Add message if it wasn't added during streaming
    if (
      this.pendingStreamingMessage &&
      this.pendingStreamingMessage.id === data.id
    ) {
      this.app.$store.dispatch(
        'conversation/addOrUpdateMessage',
        this.pendingStreamingMessage
      );
      this.pendingStreamingMessage = null;
    }

    // Mark streaming as complete with error
    this.app.$store.dispatch('conversation/addOrUpdateMessage', {
      ...data,
      streaming: false,
    });

    // Hide typing indicator
    this.app.$store.dispatch('conversation/toggleAgentTyping', {
      status: 'off',
    });

    console.error('AI streaming error:', data.error);

    // Clean up streaming state on error
    this.cleanupStreamingState(data.id);
  };

  // Chunk sequencing and queuing methods
  isValidChunkSequence(messageId, chunkSequence) {
    const expected = this.getExpectedSequence(messageId);
    return chunkSequence >= expected && chunkSequence <= expected + 5;
  }

  getExpectedSequence(messageId) {
    return this.chunkSequences[messageId] || 0;
  }

  updateChunkSequence(messageId, chunkSequence) {
    this.chunkSequences[messageId] = chunkSequence + 1;
  }

  queueChunkForLater(data) {
    const messageId = data.id;
    if (!this.queuedChunks[messageId]) {
      this.queuedChunks[messageId] = [];
    }
    this.queuedChunks[messageId].push(data);
    this.queuedChunks[messageId].sort(
      (a, b) => (a.chunk_sequence || 0) - (b.chunk_sequence || 0)
    );
  }

  processQueuedChunks(messageId) {
    if (!this.queuedChunks[messageId]) return;

    const chunks = this.queuedChunks[messageId];
    const processedChunks = [];

    chunks.forEach(chunk => {
      if (this.isValidChunkSequence(messageId, chunk.chunk_sequence)) {
        this.onAiStreamingChunk(chunk);
        processedChunks.push(chunk);
      }
    });

    // Remove processed chunks
    this.queuedChunks[messageId] = chunks.filter(
      chunk => !processedChunks.includes(chunk)
    );

    // Clean up if no more queued chunks
    if (this.queuedChunks[messageId].length === 0) {
      delete this.queuedChunks[messageId];
    }
  }

  cleanupStreamingState(messageId) {
    delete this.chunkSequences[messageId];
    delete this.queuedChunks[messageId];
    this.clearStreamingHeartbeat(messageId);
  }

  // Heartbeat mechanism for streaming
  startStreamingHeartbeat(messageId) {
    this.clearStreamingHeartbeat(messageId);

    // Set a 60-second timeout to detect stalled streaming
    this.streamingHeartbeats[messageId] = setTimeout(() => {
      console.warn(
        `[ActionCable] Streaming heartbeat timeout for message ${messageId}`
      );
      this.handleStreamingTimeout(messageId);
    }, 60000);
  }

  clearStreamingHeartbeat(messageId) {
    if (this.streamingHeartbeats[messageId]) {
      clearTimeout(this.streamingHeartbeats[messageId]);
      delete this.streamingHeartbeats[messageId];
    }
  }

  handleStreamingTimeout(messageId) {
    console.error(
      `[ActionCable] Streaming timeout detected for message ${messageId}`
    );

    // Mark message as having streaming error
    this.app.$store.dispatch('conversation/addOrUpdateMessage', {
      id: messageId,
      streaming: false,
      content: 'Streaming response timed out. Please try again.',
      additional_attributes: {
        streaming_timeout: true,
      },
    });

    // Hide typing indicator
    this.app.$store.dispatch('conversation/toggleAgentTyping', {
      status: 'off',
    });

    // Clean up state
    this.cleanupStreamingState(messageId);
  }
}

export default ActionCableConnector;
